/**
 * Un appel « coincé » : lancé, jamais conclu, et que plus rien ne fera bouger.
 *
 * 🚨 La règle d'avant marquait « injoignable » TOUT appel dont le statut
 * n'était ni completed ni failed au bout de cinq minutes. Or une conversation
 * en cours reste en `connected_to_agent` du début à la fin : une vraie
 * discussion de plus de cinq minutes était donc étiquetée injoignable, avec
 * son enregistrement audio à côté. Constaté par Kevin le 2026-09-30 dans
 * l'historique. Les meilleurs appels étaient précisément ceux qui se faisaient
 * détruire.
 *
 * Un appel décroché n'est jamais coincé : il est fini, ou il est en cours.
 */

/** Les statuts d'AVANT le décroché. Après, quelqu'un est au bout du fil. */
export const PRE_ANSWER_STATUSES = ['dialing', 'ringing', 'ai_analyzing'] as const;

export interface CallLike {
  call_status?: string | null;
  external_call_id?: string | null;
  started_at?: string | null;
  created_date?: string | null;
  recording_url?: string | null;
  duration?: number | null;
  transcription?: string | null;
}

/** Depuis combien de minutes l'appel a-t-il commencé. */
export function ageInMinutes(call: CallLike, now: Date): number {
  const raw = call.started_at || call.created_date || '';
  const at = raw ? new Date(raw).getTime() : NaN;
  if (!Number.isFinite(at)) return 0;
  return (now.getTime() - at) / 60000;
}

/**
 * Vrai seulement pour un appel qui n'a jamais abouti et que rien ne conclura.
 * Deux minutes sans identifiant d'opérateur, cinq minutes avec.
 */
export function isStuckCall(call: CallLike, now: Date): boolean {
  const status = call.call_status || '';
  // Décroché, en cours ou déjà conclu : ce n'est pas un appel coincé.
  if (!(PRE_ANSWER_STATUSES as readonly string[]).includes(status)) return false;
  // Une trace de conversation prouve que l'appel a eu lieu, quel que soit le
  // statut resté en l'air.
  if (call.recording_url || call.transcription || (call.duration ?? 0) > 0) return false;

  const age = ageInMinutes(call, now);
  return call.external_call_id ? age > 5 : age > 2;
}
